// src/pages/ManageTaskPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Inbox,
  Check,
  X,
  User as UserIcon,
  Clock,
  Calendar,
  MessageCircle,
  Edit,
  DollarSign,
  ExternalLink,
  Star,
} from "lucide-react";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import BackButton from "../components/BackButton";
import VerificationBadge from "../components/common/VerificationBadge";
import UserRating from "../components/common/UserRating";
import AvatarComponent from "../components/Avatar";
import ReviewModal from "../components/ReviewModal";
import { format, formatDistanceToNow } from "date-fns";
import { parseDate } from "../utils/helpers/date";

// Utility function to validate if a string is a URL
const isValidURL = (string) => {
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

// Utility function to get domain name from URL
const getDomainName = (url) => {
  try {
    const domain = new URL(url).hostname;
    return domain.replace('www.', '');
  } catch (_) {
    return url;
  }
};

/* ============== THEME ============== */
const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    primaryText: "#3b82f6",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    success: "#059669",
    ok: "#16a34a",
    danger: "#dc2626",
    purple: "#7c3aed",
    blueDeep: "#2563eb", // Updated to match brand colors
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.04)",
    md: "0 6px 16px rgba(15, 23, 42, 0.06)",
    lg: "0 10px 24px rgba(15, 23, 42, 0.08)",
  },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  borderRadius: "14px",
};

/* ============== PAGE ============== */
const Page = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background};
`;

const Container = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 16px;
  @media (max-width: 1024px) {
    padding: 12px;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }
  @media (max-width: 768px) {
    padding: 10px;
  }
  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 1rem;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius};
  padding: 0.875rem 1rem;
  box-shadow: ${theme.shadows.sm};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 768px) {
    grid-auto-flow: row;
    gap: 8px;
    justify-items: start;
  }
`;

const TitleWrap = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const EditIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  flex-shrink: 0;
  margin-left: 8px;
  vertical-align: middle;

  &:hover {
    background: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    color: white;
  }

  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    margin-left: 6px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const Title = styled.h1`
  margin: 0;
  color: ${theme.colors.textHeading};
  font-size: 1.45rem;
  font-weight: 700;
  letter-spacing: 0;
  overflow-wrap: anywhere;
  display: inline;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.3;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const SubTitle = styled.div`
  margin-top: 4px;
  color: ${theme.colors.textMuted};
  font-size: 0.9rem;
`;

/* ——— Mobile: 2x2 grid of action buttons ——— */
const ButtonBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 6px;

    & > button {
      width: 100%;
      min-width: 0;
      font-size: 0.85rem;
      padding: 12px 16px;
      justify-content: center;
    }
  }
`;

/* ============== Buttons (flatter) ============== */
const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
`;

const PrimaryBtn = styled(Btn)`
  background: ${theme.colors.primary};
  color: #ffffff;
  border: 1px solid ${theme.colors.primary};
  box-shadow: none;

  &:hover {
    background: ${theme.colors.blueDeep};
    border-color: ${theme.colors.blueDeep};
  }
`;

const QuietBtn = styled(Btn)`
  background: #ffffff;
  color: ${theme.colors.textBody};
  border: 1px solid ${theme.colors.border};
  box-shadow: none;

  &:hover {
    background: ${theme.colors.primaryLight};
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
  }
`;

const SuccessBtn = styled(PrimaryBtn)`
  background: ${theme.colors.ok};
  border-color: ${theme.colors.ok};

  &:hover {
    background: ${theme.colors.success};
    border-color: ${theme.colors.success};
  }
`;

const DangerBtn = styled(Btn)`
  background: #ffffff;
  color: #dc2626;
  border: 1px solid #fecaca;
  box-shadow: none;

  &:hover {
    background: #fef2f2;
    color: #b91c1c;
    border-color: #fca5a5;
  }
`;

/* ============== Card (clean, airy) ============== */
const Card = styled.section`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.shadows.sm};
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid ${theme.colors.border};
  transition: border-color 0.2s ease;

  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }
`;

/* thin separators (no heavy boxes) */
const Divider = styled.hr`
  border: 0;
  height: 1px;
  background: ${theme.colors.border};
  opacity: 0.6;
  margin: 0.75rem 0;
`;

/* ============== Status / Stats ============== */
const StatusRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
`;

const StatusTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.textHeading};
  font-size: 1.02rem;
  font-weight: 800;
`;

const badgeColors = {
  pending: { bg: "hsl(35, 91%, 95%)", fg: "hsl(35, 91%, 45%)" },
  accepted: { bg: "hsl(145, 63%, 95%)", fg: "hsl(145, 63%, 35%)" },
  open: { bg: "hsl(145, 63%, 95%)", fg: "hsl(145, 63%, 35%)" },
  rejected: { bg: "hsl(0, 84%, 95%)", fg: "hsl(0, 84%, 45%)" },
  withdrawn: { bg: "hsl(0, 84%, 95%)", fg: "hsl(0, 84%, 45%)" },
  in_progress: { bg: "#f0f7ff", fg: "#3b82f6" },
  completed: { bg: "hsl(145, 63%, 95%)", fg: "hsl(145, 63%, 35%)" },
  cancelled: { bg: "hsl(0, 84%, 95%)", fg: "hsl(0, 84%, 45%)" },
};

const StatusBadge = styled.span`
  background: ${({ status }) => badgeColors[status]?.bg || "#f0f7ff"};
  color: ${({ status }) => badgeColors[status]?.fg || theme.colors.primary};
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  border: 1px solid rgba(0, 0, 0, 0.05);
  letter-spacing: -0.01em;
  flex-shrink: 0;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  padding: 10px 0;
`;

const StatLabel = styled.div`
  color: ${theme.colors.textMuted};
  font-weight: 700;
  font-size: 0.86rem;
`;

const StatValue = styled.div`
  color: ${theme.colors.textHeading};
  font-weight: 900;
  margin-top: 2px;
`;

/* ============== Tabs (simple pills) ============== */
const Tabs = styled.div`
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;
const TabBar = styled.div`
  display: flex;
  gap: 6px;
  background: #ffffff;
  border-radius: 10px;
  padding: 4px;
  border: 1px solid ${theme.colors.border};
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;

  @media (max-width: 768px) {
    width: 100%;
    gap: 1px;
    padding: 3px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const TabBtn = styled.button`
  flex: 1;
  background: ${(props) => (props.active ? theme.colors.primaryLight : "transparent")};
  border: 1px solid ${(props) => (props.active ? theme.colors.primaryLight : "transparent")};
  border-radius: 8px;
  padding: 6px 10px;
  font-weight: 600;
  font-size: 0.82rem;
  color: ${(props) => (props.active ? theme.colors.primary : theme.colors.textMuted)};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  box-shadow: none;
  white-space: nowrap;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;

  &:hover {
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primaryLight};
  }

  @media (max-width: 768px) {
    padding: 10px 8px;
    font-size: 0.8rem;
    font-weight: 600;
    min-width: 0;
    flex: 1;
  }
  
  @media (max-width: 480px) {
    padding: 10px 6px;
    font-size: 0.75rem;
  }
`;

/* ============== Offer item (separate cards, more spacing) ============== */
const OfferCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius};
  padding: 0.875rem;
  box-shadow: none;
  margin-bottom: 0.75rem;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 0.75rem;
    border-radius: 12px;
  }
`;

const OfferContent = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const OfferHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0;

  @media (max-width: 768px) {
    flex-direction: row;
    gap: 0.75rem;
    align-items: flex-start;
  }
`;

const UserBlock = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 0.625rem;
  }
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: ${theme.colors.primary};
  overflow: hidden;
  border: 1px solid ${theme.colors.border};
  flex-shrink: 0;
  font-weight: 600;
  font-size: 0.875rem;
  box-shadow: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    font-size: 0.75rem;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: 600;
  color: ${theme.colors.textHeading};
  cursor: pointer;
  text-decoration: none;
  transition: color 0.15s ease;
  font-size: 1rem;
  letter-spacing: -0.01em;
  line-height: 1.3;

  &:hover {
    color: ${theme.colors.primary};
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const UserMeta = styled.div`
  font-size: 0.8125rem;
  color: ${theme.colors.textMuted};
  font-weight: 500;
  line-height: 1.4;
`;

const OfferStatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  margin: 0;
  box-shadow: none;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.875rem 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.625rem;
    padding: 0.75rem 0.625rem;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex: 1;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
    gap: 0;
  }
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${({ bg }) => bg || theme.colors.primaryLight};
  color: ${({ color }) => color || theme.colors.primary};
  flex-shrink: 0;
  box-shadow: none;
  border: 1px solid ${theme.colors.border};
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }
`;

const OfferStatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;

  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: left;
    flex: 1;
  }
`;

const OfferStatValue = styled.div`
  font-weight: 600;
  color: ${theme.colors.textHeading};
  font-size: 0.85rem;
  letter-spacing: -0.01em;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const OfferStatLabel = styled.div`
  font-size: 0.72rem;
  color: ${theme.colors.textMuted};
  font-weight: 500;
  line-height: 1.1;
`;

const OfferChat = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed ${theme.colors.border};
`;

const OfferChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  font-size: 0.95rem;
`;

const OfferChatCount = styled.span`
  color: ${theme.colors.textMuted};
  font-size: 0.85rem;
  font-weight: 500;
`;

const OfferChatEmpty = styled.div`
  color: ${theme.colors.textMuted};
  font-size: 0.85rem;
  margin-bottom: 6px;
`;

const OfferChatToggle = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: ${theme.colors.primaryText};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  margin-left: auto;

  &:hover {
    text-decoration: underline;
  }
`;

const OfferChatMessages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const OfferChatMessage = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const OfferChatBubble = styled.div`
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: 6px 8px;
  max-width: 520px;
`;

const OfferChatMeta = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 0.8rem;
  color: ${theme.colors.textMuted};
  margin-bottom: 4px;
`;

const OfferChatText = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.textBody};
  line-height: 1.35;
`;

const ExperienceTag = styled.span`
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  color: ${theme.colors.textBody};
  font-weight: 600;
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${theme.colors.border};
  letter-spacing: -0.01em;
  white-space: nowrap;
  box-shadow: none;
`;

const SectionTitle = styled.h4`
  margin: 0 0 0.375rem;
  color: ${theme.colors.textHeading};
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const SectionContent = styled.div`
  margin: 0;

  p {
    margin: 0;
    color: ${theme.colors.textBody};
    line-height: 1.5;
    font-size: 0.85rem;
  }
`;

const SectionDescription = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius};
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.85rem;
  color: ${theme.colors.textBody};
  line-height: 1.5;
  box-shadow: none;

  strong {
    color: ${theme.colors.textHeading};
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 0.8rem;
    margin-bottom: 1rem;
  }
`;

const PortfolioLinks = styled.div`
  margin-top: 1rem;
`;

const PortfolioLinksTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${theme.colors.textHeading};
`;

const PortfolioLinksList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const PortfolioLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textBody};
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const Skeleton = styled.div`
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite linear;
  height: ${(p) => p.h || "14px"};
  width: ${(p) => p.w || "100%"};

  @keyframes shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 0.625rem;
  justify-content: flex-end;
  align-items: center;
  margin-top: 0;
  padding-top: 1rem;
  border-top: 1px solid ${theme.colors.border};

  @media (max-width: 768px) {
    justify-content: stretch;
    gap: 0.5rem;
    padding-top: 0.875rem;
    flex-direction: column;

    button {
      width: 100%;
      font-size: 0.875rem;
      padding: 0.75rem 1rem;
      justify-content: center;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

/* ============== Question Card Styles ============== */
const QuestionCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius};
  padding: 12px;
  margin-bottom: 12px;
  transition: border-color 0.2s ease;
`;

const QuestionHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const QuestionUserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const QuestionUserName = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: capitalize;
`;

const QuestionMeta = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  display: flex;
  align-items: center;
  margin-top: 2px;
`;

const QuestionText = styled.div`
  color: #334155;
  line-height: 1.7;
  font-size: 0.9rem;
  padding: 10px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid ${theme.colors.border};
  margin-bottom: 12px;
`;

const RepliesContainer = styled.div`
  margin-top: 0;
  margin-left: 52px;
  padding: 10px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid ${theme.colors.border};
`;

const ReplyItem = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ReplyHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const ReplyUserName = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: #0f172a;
  cursor: pointer;
  &:hover {
    color: #3b82f6;
  }
`;

const ReplyTimestamp = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  display: flex;
  align-items: center;
`;

const ReplyTextStyled = styled.div`
  font-size: 0.85rem;
  color: #334155;
  line-height: 1.6;
`;

const ReplyForm = styled.div`
  margin-top: 10px;
  padding: 10px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid ${theme.colors.border};
`;

const ReplyTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const ReplyActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ReplyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.12s ease;

  &.primary {
    background: ${theme.colors.primary};
    color: white;
    border: 1px solid ${theme.colors.primary};

    &:hover {
      background: ${theme.colors.blueDeep};
    }
  }

  &.secondary {
    background: white;
    color: ${theme.colors.textBody};
    border: 1px solid ${theme.colors.border};

    &:hover {
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
    }
  }
`;

/* ============== Modal Styles ============== */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.2s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 768px) {
    margin: 20px;
    padding: 20px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px;
  color: ${theme.colors.textHeading};
  font-size: 1.25rem;
  font-weight: 700;
`;

const ModalDescription = styled.p`
  margin: 0 0 20px;
  color: ${theme.colors.textBody};
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.95rem;
  resize: vertical;
  margin-bottom: 20px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const ModalButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.12s ease;

  &.primary {
    background: #dc2626;
    color: white;
    border: 1px solid #dc2626;

    &:hover {
      background: #b91c1c;
    }
  }

  &.secondary {
    background: white;
    color: ${theme.colors.textBody};
    border: 1px solid ${theme.colors.border};

    &:hover {
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
    }
  }
`;

/* ============== Component ============== */
const ManageTaskPage = ({ state }) => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [task, setTask] = useState(null);
  const [offers, setOffers] = useState([]);
  const [questions, setQuestions] = useState([]);
  
  // Initialize activeTab based on URL parameter, default to "pending"
  const tabFromUrl = searchParams.get('tab');
  const validTabs = ['pending', 'accepted', 'declined', 'questions'];
  const initialTab = validTabs.includes(tabFromUrl) ? tabFromUrl : 'pending';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingOfferId, setRejectingOfferId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [replySubmitting, setReplySubmitting] = useState(false);

  // Edit question states
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editQuestionText, setEditQuestionText] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Edit reply states
  const [editingReply, setEditingReply] = useState(null);
  const [editReplyText, setEditReplyText] = useState("");
  const [editReplySubmitting, setEditReplySubmitting] = useState(false);

  // Offer discussion states (per-offer)
  const [offerQuestionsById, setOfferQuestionsById] = useState({});
  const [offerQuestionsLoadingById, setOfferQuestionsLoadingById] = useState({});
  const [offerQuestionTextById, setOfferQuestionTextById] = useState({});
  const [offerQuestionSubmittingById, setOfferQuestionSubmittingById] = useState({});

  // Review modal states
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [offerChatExpandedById, setOfferChatExpandedById] = useState({});

  const handleImageError = (userId) => {
    setImageErrors(prev => ({ ...prev, [userId]: true }));
  };

  const loadOfferQuestions = useCallback(async (offerId) => {
    if (!offerId) return;
    setOfferQuestionsLoadingById((p) => ({ ...p, [offerId]: true }));
    try {
      const { data } = await request("POST", API_CONSTANT_MAP.fetch_task_questions, {
        task_id: taskId,
        offer_id: offerId,
      });
      setOfferQuestionsById((p) => ({
        ...p,
        [offerId]: data?.success ? data.questions : [],
      }));
    } catch (err) {
      console.error("Error fetching offer questions:", err);
    } finally {
      setOfferQuestionsLoadingById((p) => ({ ...p, [offerId]: false }));
    }
  }, [taskId]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await request("POST", API_CONSTANT_MAP.fetch_task_by_id, {
          task_id: taskId,
        });
        setTask(resp.data.task);
        setOffers(resp.data.task.offers || []);

        // Fetch questions for this task
        const questionsResp = await request(
          "POST",
          API_CONSTANT_MAP.fetch_task_questions,
          {
            task_id: taskId,
          }
        );
        setQuestions(questionsResp.data?.questions || []);

        // Fetch existing review for this task (if any)
        try {
          const reviewResp = await request(
            "POST",
            API_CONSTANT_MAP.fetch_task_review,
            { task_id: taskId }
          );
          if (reviewResp.data?.success && reviewResp.data.review) {
            setExistingReview(reviewResp.data.review);
          }
        } catch (reviewErr) {
          // Ignore - review fetch is optional
          console.log("No existing review or not authorized");
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [taskId]);

  useEffect(() => {
    setOfferQuestionsById({});
    setOfferQuestionsLoadingById({});
    setOfferQuestionTextById({});
    setOfferQuestionSubmittingById({});
    setOfferChatExpandedById({});
  }, [taskId]);

  useEffect(() => {
    if (!offers?.length) return;
    offers.forEach((offer) => {
      if (!offer?.id) return;
      if (offerQuestionsById[offer.id]) return;
      if (offerQuestionsLoadingById[offer.id]) return;
      loadOfferQuestions(offer.id);
    });
  }, [offers, offerQuestionsById, offerQuestionsLoadingById, loadOfferQuestions]);

  if (loading || !task) {
    return (
      <>
        <NavBar user={state.user} />
        <OuterContainer>
          <Page>
            <Container>
              {/* Header Skeleton */}
              <Header>
                <HeaderLeft>
                  <Skeleton h="20px" w="20px" />
                  <TitleWrap>
                    <Skeleton
                      h="32px"
                      w="300px"
                      style={{ marginBottom: "8px" }}
                    />
                    <Skeleton h="16px" w="400px" />
                  </TitleWrap>
                </HeaderLeft>
                <ButtonBar>
                  <Skeleton
                    h="40px"
                    w="120px"
                    style={{ borderRadius: "10px" }}
                  />
                  <Skeleton
                    h="40px"
                    w="100px"
                    style={{ borderRadius: "10px" }}
                  />
                  <Skeleton
                    h="40px"
                    w="90px"
                    style={{ borderRadius: "10px" }}
                  />
                </ButtonBar>
              </Header>

              {/* Status Card Skeleton */}
              <Card>
                <StatusRow>
                  <Skeleton h="24px" w="120px" />
                  <Skeleton
                    h="28px"
                    w="80px"
                    style={{ borderRadius: "999px" }}
                  />
                </StatusRow>
                <Divider />
                <StatsRow>
                  <Stat>
                    <Skeleton
                      h="16px"
                      w="60px"
                      style={{ marginBottom: "4px" }}
                    />
                    <Skeleton h="20px" w="80px" />
                  </Stat>
                  <Stat>
                    <Skeleton
                      h="16px"
                      w="80px"
                      style={{ marginBottom: "4px" }}
                    />
                    <Skeleton h="20px" w="40px" />
                  </Stat>
                  <Stat>
                    <Skeleton
                      h="16px"
                      w="60px"
                      style={{ marginBottom: "4px" }}
                    />
                    <Skeleton h="20px" w="100px" />
                  </Stat>
                </StatsRow>
              </Card>

              {/* Tabs Skeleton */}
              <Tabs>
                <TabBar>
                  <Skeleton
                    h="36px"
                    w="120px"
                    style={{ borderRadius: "999px" }}
                  />
                  <Skeleton
                    h="36px"
                    w="130px"
                    style={{ borderRadius: "999px" }}
                  />
                  <Skeleton
                    h="36px"
                    w="120px"
                    style={{ borderRadius: "999px" }}
                  />
                </TabBar>
              </Tabs>

              {/* Offers Skeleton */}
              {[1, 2, 3].map((i) => (
                <OfferCard key={i}>
                  <OfferContent>
                    {/* Offer Header */}
                    <OfferHeader>
                      <UserBlock>
                        <Skeleton
                          h="48px"
                          w="48px"
                          style={{ borderRadius: "50%" }}
                        />
                        <UserInfo>
                          <Skeleton
                            h="18px"
                            w="150px"
                            style={{ marginBottom: "4px" }}
                          />
                          <Skeleton h="14px" w="120px" />
                        </UserInfo>
                      </UserBlock>
                      <Skeleton
                        h="28px"
                        w="80px"
                        style={{ borderRadius: "20px" }}
                      />
                    </OfferHeader>

                    {/* Stats Row */}
                    <OfferStatsRow>
                      <StatItem>
                        <Skeleton
                          h="28px"
                          w="28px"
                          style={{ borderRadius: "6px", marginRight: "8px" }}
                        />
                        <div>
                          <Skeleton
                            h="16px"
                            w="80px"
                            style={{ marginBottom: "4px" }}
                          />
                          <Skeleton h="12px" w="100px" />
                        </div>
                      </StatItem>
                      <StatItem>
                        <Skeleton
                          h="28px"
                          w="28px"
                          style={{ borderRadius: "6px", marginRight: "8px" }}
                        />
                        <div>
                          <Skeleton
                            h="16px"
                            w="60px"
                            style={{ marginBottom: "4px" }}
                          />
                          <Skeleton h="12px" w="80px" />
                        </div>
                      </StatItem>
                      <StatItem>
                        <Skeleton
                          h="28px"
                          w="28px"
                          style={{ borderRadius: "6px", marginRight: "8px" }}
                        />
                        <div>
                          <Skeleton
                            h="16px"
                            w="70px"
                            style={{ marginBottom: "4px" }}
                          />
                          <Skeleton h="12px" w="90px" />
                        </div>
                      </StatItem>
                    </OfferStatsRow>

                    {/* Experience Tag */}
                    <Skeleton
                      h="32px"
                      w="150px"
                      style={{ borderRadius: "999px", margin: "4px 0 10px" }}
                    />

                    {/* Proposal Section */}
                    <SectionTitle style={{ marginBottom: "6px" }}>
                      <Skeleton h="18px" w="80px" />
                    </SectionTitle>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        marginBottom: "12px",
                      }}
                    >
                      <Skeleton h="16px" w="100%" />
                      <Skeleton h="16px" w="95%" />
                      <Skeleton h="16px" w="80%" />
                    </div>

                    {/* Action Buttons */}
                    <ActionBar>
                      <Skeleton
                        h="36px"
                        w="80px"
                        style={{ borderRadius: "10px" }}
                      />
                      <Skeleton
                        h="36px"
                        w="120px"
                        style={{ borderRadius: "10px" }}
                      />
                    </ActionBar>
                  </OfferContent>
                </OfferCard>
              ))}
            </Container>
          </Page>
        </OuterContainer>
      </>
    );
  }

  const pending = offers.filter((o) => o.status === "pending");
  const accepted = offers.filter((o) => o.status === "accepted");
  const declined = offers.filter((o) =>
    ["rejected", "withdrawn"].includes(o.status)
  );
  const current = { pending, accepted, declined, questions }[activeTab];

  const updateOfferStatus = async (offerId, newStatus, rejectReason = null) => {
    try {
      const payload = { offer_id: offerId, status: newStatus };
      if (newStatus === "rejected" && rejectReason) {
        payload.reject_reason = rejectReason;
      }
      
      const resp = await request(
        "POST",
        API_CONSTANT_MAP.update_task_offer_status,
        payload
      );
      if (!resp.data.success)
        throw new Error(resp.data.error?.message || "Unknown error");

      const conversationId = resp?.data?.conversation_id || null;
      if (resp.data.task_status) {
        setTask((t) => ({ ...t, status: resp.data.task_status }));
      }

      setOffers((prev) =>
        prev.map((o) => {
          if (o.id === offerId) {
            return {
              ...o,
              status: newStatus,
              auto_rejected: false,
              conversation_id:
                newStatus === "accepted"
                  ? conversationId || o.conversation_id || null
                  : o.conversation_id || null,
            };
          }
          if (newStatus === "accepted" && o.status === "pending") {
            return { ...o, status: "rejected", auto_rejected: true };
          }
          return o;
        })
      );
    } catch (err) {
      console.error(`Failed to ${newStatus} offer:`, err);
      alert(`❌ Could not ${newStatus} offer: ${err.message}`);
    }
  };

  const handleAccept = (offerId) => updateOfferStatus(offerId, "accepted");
  
  const handleDecline = (offerId) => {
    setRejectingOfferId(offerId);
    setRejectModalOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectingOfferId) return;
    
    try {
      await updateOfferStatus(rejectingOfferId, "rejected", rejectReason.trim() || null);
      
      // Close modal and reset state
      setRejectModalOpen(false);
      setRejectingOfferId(null);
      setRejectReason("");
    } catch (err) {
      console.error("Failed to reject offer:", err);
      // The error handling is already done in updateOfferStatus
    }
  };

  const handleRejectCancel = () => {
    setRejectModalOpen(false);
    setRejectingOfferId(null);
    setRejectReason("");
  };

  const handleCancelTask = () => {
    setCancelModalOpen(true);
  };

  const handleCancelTaskConfirm = async () => {
    try {
      const resp = await request("POST", API_CONSTANT_MAP.cancel_task, {
        task_id: task.id,
      });
      
      if (!resp.data.success) {
        throw new Error(resp.data.error?.message || "Failed to cancel task");
      }

      // Update task status to cancelled
      setTask((t) => ({ ...t, status: "cancelled", cancelled_at: new Date().toISOString() }));
      
      // Close modal
      setCancelModalOpen(false);
      
      alert("✅ Task has been cancelled successfully");
    } catch (err) {
      console.error("Failed to cancel task:", err);
      alert(`❌ Could not cancel task: ${err.message}`);
    }
  };

  const handleCancelTaskCancel = () => {
    setCancelModalOpen(false);
  };

  const handleMarkComplete = async () => {
    try {
      const resp = await request("POST", API_CONSTANT_MAP.update_task_status, {
        task_id: task.id,
        status: "completed",
      });
      if (!resp.data.success)
        throw new Error(resp.data.error?.message || "Unknown error");
      setTask({ ...task, status: "completed" });

      // Show review modal if there are users to review (offers or questions)
      const hasReviewableUsers = offers.length > 0 || questions.length > 0;
      if (hasReviewableUsers) {
        setReviewModalOpen(true);
      }
    } catch (err) {
      console.error("Failed to mark complete:", err);
      alert(`❌ Could not complete task: ${err.message}`);
    }
  };

  const handleSubmitReview = async (reviewData) => {
    try {
      setReviewSubmitting(true);
      const resp = await request("POST", API_CONSTANT_MAP.submit_review, {
        task_id: task.id,
        ...reviewData,
      });
      if (!resp.data.success) {
        throw new Error(resp.data.error?.message || "Failed to submit review");
      }
      // Update existingReview state with the new/updated review
      setExistingReview(resp.data.review);
      setReviewModalOpen(false);
      alert(resp.data.is_edit ? "Review updated!" : "Thank you for your review!");
    } catch (err) {
      console.error("Failed to submit review:", err);
      alert(`❌ ${err.message}`);
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleCancelCollaboration = async (acceptedOfferId) => {
    try {
      const resp = await request(
        "POST",
        API_CONSTANT_MAP.update_task_offer_status,
        { offer_id: acceptedOfferId, status: "withdrawn" }
      );
      if (!resp.data.success) throw new Error(resp.data.error?.message);
      setTask((t) => ({ ...t, status: resp.data.task_status }));
      setOffers((list) =>
        list.map((o) => {
          if (o.id === acceptedOfferId) return { ...o, status: "withdrawn" };
          if (o.auto_rejected)
            return { ...o, status: "pending", auto_rejected: false };
          return o;
        })
      );
    } catch (err) {
      console.error("Cancel collaboration failed:", err);
      alert(`❌ ${err.message}`);
    }
  };

  const acceptedWithChat = accepted.find((o) => !!o.conversation_id);

  const handleReplySubmit = async (questionId) => {
    if (!replyText.trim() || replySubmitting) return;

    try {
      setReplySubmitting(true);
      const response = await request(
        "POST",
        API_CONSTANT_MAP.post_task_question_reply,
        {
          question_id: questionId,
          reply_text: replyText.trim(),
        }
      );

      if (response.data?.success) {
        // Refresh questions to show the new reply
        const questionsResp = await request(
          "POST",
          API_CONSTANT_MAP.fetch_task_questions,
          {
            task_id: taskId,
          }
        );
        setQuestions(questionsResp.data?.questions || []);

        // Reset reply form
        setReplyingTo(null);
        setReplyText("");
      } else {
        alert("Failed to submit reply. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
      alert("Error submitting reply. Please try again.");
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleOfferQuestionSubmit = async (offerId) => {
    const text = (offerQuestionTextById[offerId] || "").trim();
    if (!text || offerQuestionSubmittingById[offerId]) return;
    try {
      setOfferQuestionSubmittingById((p) => ({ ...p, [offerId]: true }));
      const payload = {
        task_id: taskId,
        offer_id: offerId,
        question_text: text,
      };
      const { data } = await request(
        "POST",
        API_CONSTANT_MAP.submit_task_question,
        payload
      );
      if (data?.success) {
        setOfferQuestionsById((p) => ({
          ...p,
          [offerId]: [data.question, ...(p[offerId] || [])],
        }));
        setOfferQuestionTextById((p) => ({ ...p, [offerId]: "" }));
      } else {
        alert(data?.error?.message || "Failed to post message.");
      }
    } catch (error) {
      console.error("Failed to submit offer message:", error);
      alert("Failed to submit message. Please try again.");
    } finally {
      setOfferQuestionSubmittingById((p) => ({ ...p, [offerId]: false }));
    }
  };

  // Edit question handlers
  const handleEditQuestion = (question) => {
    setEditingQuestion(question.id);
    setEditQuestionText(question.question_text || question.text);
    setReplyingTo(null);
    setReplyText("");
  };

  const handleEditSubmit = async (questionId) => {
    if (!editQuestionText.trim() || editSubmitting) return;

    try {
      setEditSubmitting(true);
      const response = await request(
        "POST",
        API_CONSTANT_MAP.edit_task_question,
        {
          question_id: questionId,
          question_text: editQuestionText.trim(),
        }
      );

      if (response.data?.success) {
        const questionsResp = await request(
          "POST",
          API_CONSTANT_MAP.fetch_task_questions,
          { task_id: taskId }
        );
        setQuestions(questionsResp.data?.questions || []);
        setEditingQuestion(null);
        setEditQuestionText("");
      } else {
        alert(response.data?.error?.message || "Failed to update question. Please try again.");
      }
    } catch (error) {
      console.error("Error updating question:", error);
      alert("Error updating question. Please try again.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditQuestionText("");
  };

  // Edit reply handlers
  const handleEditReply = (reply) => {
    setEditingReply(reply.id);
    setEditReplyText(reply.reply_text || reply.text);
    setEditingQuestion(null);
    setEditQuestionText("");
  };

  const handleEditReplySubmit = async (replyId) => {
    if (!editReplyText.trim() || editReplySubmitting) return;

    try {
      setEditReplySubmitting(true);
      const response = await request(
        "POST",
        API_CONSTANT_MAP.edit_task_question_reply,
        {
          reply_id: replyId,
          reply_text: editReplyText.trim(),
        }
      );

      if (response.data?.success) {
        const questionsResp = await request(
          "POST",
          API_CONSTANT_MAP.fetch_task_questions,
          { task_id: taskId }
        );
        setQuestions(questionsResp.data?.questions || []);
        setEditingReply(null);
        setEditReplyText("");
      } else {
        alert(response.data?.error?.message || "Failed to update reply. Please try again.");
      }
    } catch (error) {
      console.error("Error updating reply:", error);
      alert("Error updating reply. Please try again.");
    } finally {
      setEditReplySubmitting(false);
    }
  };

  const handleCancelEditReply = () => {
    setEditingReply(null);
    setEditReplyText("");
  };

  return (
    <>
      <NavBar user={state.user} />
      <OuterContainer>
        <Page>
          <Container>
            <BackButton onClick={() => navigate(-1)} size={16} title="Back" />
            <Header>
              <HeaderLeft>
                <TitleWrap>
                  <div>
                    <TitleRow>
                      <Title
                        style={{ textTransform: "capitalize", cursor: "pointer" }}
                        onClick={() => navigate(`/tasks/${taskId}`)}
                        title="Click to view task page"
                      >
                        {task.title}
                      </Title>
                      <EditIcon
                        onClick={() => navigate(`/edit/task/${taskId}`)}
                        title="Edit task details"
                      >
                        <Edit size={14} />
                      </EditIcon>
                    </TitleRow>
                    <SubTitle>
                      Manage offers and communication for your task
                    </SubTitle>
                  </div>
                </TitleWrap>
              </HeaderLeft>

              <ButtonBar>
                {acceptedWithChat?.conversation_id && (
                  <PrimaryBtn
                    onClick={() =>
                      navigate(`/chat/${acceptedWithChat.conversation_id}`)
                    }
                    title="Open project chat"
                  >
                    Project Chat
                  </PrimaryBtn>
                )}
                {task.status === "in_progress" && (
                  <SuccessBtn onClick={handleMarkComplete}>
                    Mark as Complete
                  </SuccessBtn>
                )}
                {(task.status === "open" || task.status === "in_progress") && (
                  <DangerBtn onClick={handleCancelTask} title="Cancel this task">
                    Cancel Task
                  </DangerBtn>
                )}
              </ButtonBar>
            </Header>

            <Card>
              <StatusRow>
                <StatusTitle>Task Status</StatusTitle>
                <StatusBadge status={task.status}>
                  {task.status
                    .split("_")
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(" ")}
                </StatusBadge>
              </StatusRow>

              <Divider />

              <StatsRow>
                <Stat>
                  <StatLabel>Budget</StatLabel>
                  <StatValue>${Number(task.budget).toFixed(2)}{task.budget_type === 'hourly' ? '/hr' : ''}</StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Total Offers</StatLabel>
                  <StatValue>{offers.length}</StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Created</StatLabel>
                  <StatValue>
                    {format(parseDate(task.created_at), "MMM d, yyyy")}
                  </StatValue>
                </Stat>
              </StatsRow>

              {/* Review button for completed tasks */}
              {task.status === "completed" && (offers.length > 0 || questions.length > 0) && (
                <>
                  <Divider />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Star size={18} style={{ color: existingReview ? "#f59e0b" : "#9ca3af", fill: existingReview ? "#f59e0b" : "none" }} />
                      <span style={{ fontSize: "0.9rem", color: theme.colors.textBody }}>
                        {existingReview
                          ? `You rated ${existingReview.rating}/5 stars`
                          : "Leave a review for your helper"}
                      </span>
                    </div>
                    <button
                      onClick={() => setReviewModalOpen(true)}
                      style={{
                        background: existingReview ? "transparent" : theme.colors.primary,
                        color: existingReview ? theme.colors.primary : "white",
                        border: existingReview ? `1px solid ${theme.colors.primary}` : "none",
                        padding: "8px 16px",
                        borderRadius: 8,
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {existingReview ? (
                        <>
                          <Edit size={14} />
                          Edit Review
                        </>
                      ) : (
                        <>
                          <Star size={14} />
                          Leave Review
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </Card>


            <Tabs>
              <TabBar>
                <TabBtn
                  active={activeTab === "pending"}
                  onClick={() => setActiveTab("pending")}
                >
                  Pending ({pending.length})
                </TabBtn>
                <TabBtn
                  active={activeTab === "accepted"}
                  onClick={() => setActiveTab("accepted")}
                >
                  Accepted ({accepted.length})
                </TabBtn>
                <TabBtn
                  active={activeTab === "declined"}
                  onClick={() => setActiveTab("declined")}
                >
                  Declined ({declined.length})
                </TabBtn>
                <TabBtn
                  active={activeTab === "questions"}
                  onClick={() => setActiveTab("questions")}
                >
                  Questions ({questions.length})
                </TabBtn>
              </TabBar>
            </Tabs>

            {/* Section Descriptions */}
            {activeTab === "pending" && (
              <SectionDescription>
                <strong>Pending Offers:</strong> {task.status === "cancelled" 
                  ? "This task has been cancelled. All pending offers have been automatically declined." 
                  : (
                    <>
                      New offers from freelancers waiting for your review. <strong>Accepting lets you chat directly</strong> to discuss details, ask questions, and plan the work. You can <strong>cancel anytime</strong> if needed. No commitment pressure - you stay in control!
                    </>
                  )
                }
              </SectionDescription>
            )}

            {activeTab === "accepted" && (
              <SectionDescription>
                <strong>Accepted Offers:</strong> Active collaborations with freelancers you've chosen. Use the <strong>project chat</strong> to discuss progress, share files, and coordinate work. Remember, you can <strong>cancel anytime</strong> if things aren't working out.
              </SectionDescription>
            )}

            {activeTab === "declined" && (
              <SectionDescription>
                <strong>Declined Offers:</strong> These are offers you've
                rejected. This is for your reference to track what offers you've
                previously reviewed and declined.
              </SectionDescription>
            )}

            {activeTab === "questions" && (
              <SectionDescription>
                <strong>Questions:</strong> Freelancers ask questions to better understand your needs before submitting offers. <strong>Answering promptly</strong> helps clarify requirements, builds trust, and attracts higher-quality proposals from interested professionals.
              </SectionDescription>
            )}

            {activeTab === "questions" ? (
              questions.length > 0 ? (
                questions.map((q) => (
                  <QuestionCard key={q.id}>
                    <QuestionHeader>
                      <AvatarComponent
                        src={q.avatar}
                        name={q.user_name}
                        userId={q.user_id}
                        size="40px"
                      />
                      <QuestionUserInfo>
                        <QuestionUserName>
                          <Link to={`/users/${q.user_id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                            {q.user_name || "Anonymous User"}
                          </Link>
                          <UserRating
                            userId={q.user_id}
                            averageRating={q.average_rating}
                            totalReviews={q.total_reviews}
                          />
                        </QuestionUserName>
                        <QuestionMeta>
                          {formatDistanceToNow(parseDate(q.created_at), { addSuffix: true })}
                          {q.user_id === state?.user?.user_id && editingQuestion !== q.id && (
                            <span
                              onClick={() => handleEditQuestion(q)}
                              style={{
                                marginLeft: '8px',
                                color: '#6b7280',
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                              }}
                              onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                              onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                            >
                              · Edit
                            </span>
                          )}
                        </QuestionMeta>
                      </QuestionUserInfo>
                    </QuestionHeader>

                    {editingQuestion === q.id ? (
                      <ReplyForm style={{ marginBottom: '16px' }}>
                        <ReplyTextarea
                          value={editQuestionText}
                          onChange={(e) => setEditQuestionText(e.target.value)}
                          placeholder="Edit your question..."
                        />
                        <ReplyActions>
                          <ReplyButton
                            className="secondary"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </ReplyButton>
                          <ReplyButton
                            className="primary"
                            onClick={() => handleEditSubmit(q.id)}
                            disabled={!editQuestionText.trim() || editSubmitting}
                          >
                            {editSubmitting ? "Saving..." : "Save"}
                          </ReplyButton>
                        </ReplyActions>
                      </ReplyForm>
                    ) : (
                      <QuestionText>{q.question_text || q.text}</QuestionText>
                    )}

                    {/* Display replies if they exist */}
                    {q.replies && q.replies.length > 0 && (
                      <RepliesContainer>
                        {q.replies.map((reply) => (
                          <ReplyItem key={reply.id}>
                            <ReplyHeaderStyled>
                              <AvatarComponent
                                src={reply.avatar}
                                name={reply.user_name}
                                userId={reply.user_id}
                                size="24px"
                              />
                              <ReplyUserName onClick={() => navigate(`/users/${reply.user_id}`)}>
                                {reply.user_name || "Anonymous User"}
                              </ReplyUserName>
                              <UserRating
                                userId={reply.user_id}
                                averageRating={reply.average_rating}
                                totalReviews={reply.total_reviews}
                              />
                              <ReplyTimestamp>
                                {formatDistanceToNow(parseDate(reply.created_at), { addSuffix: true })}
                                {reply.user_id === state?.user?.user_id && editingReply !== reply.id && (
                                  <span
                                    onClick={() => handleEditReply(reply)}
                                    style={{
                                      marginLeft: '8px',
                                      color: '#6b7280',
                                      cursor: 'pointer',
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                                    onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                                  >
                                    · Edit
                                  </span>
                                )}
                              </ReplyTimestamp>
                            </ReplyHeaderStyled>
                            {editingReply === reply.id ? (
                              <ReplyForm style={{ marginTop: '8px', marginBottom: '16px' }}>
                                <ReplyTextarea
                                  value={editReplyText}
                                  onChange={(e) => setEditReplyText(e.target.value)}
                                  placeholder="Edit your reply..."
                                />
                                <ReplyActions>
                                  <ReplyButton
                                    className="secondary"
                                    onClick={handleCancelEditReply}
                                  >
                                    Cancel
                                  </ReplyButton>
                                  <ReplyButton
                                    className="primary"
                                    onClick={() => handleEditReplySubmit(reply.id)}
                                    disabled={!editReplyText.trim() || editReplySubmitting}
                                  >
                                    {editReplySubmitting ? "Saving..." : "Save"}
                                  </ReplyButton>
                                </ReplyActions>
                              </ReplyForm>
                            ) : (
                              <ReplyTextStyled>{reply.reply_text || reply.text}</ReplyTextStyled>
                            )}
                          </ReplyItem>
                        ))}
                      </RepliesContainer>
                    )}

                    {/* Reply form for task poster */}
                    {replyingTo === q.id ? (
                      <ReplyForm>
                        <ReplyTextarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Write your reply..."
                        />
                        <ReplyActions>
                          <ReplyButton
                            className="secondary"
                            onClick={() => {
                              setReplyingTo(null);
                              setReplyText("");
                            }}
                          >
                            Cancel
                          </ReplyButton>
                          <ReplyButton
                            className="primary"
                            onClick={() => handleReplySubmit(q.id)}
                            disabled={!replyText.trim() || replySubmitting}
                          >
                            {replySubmitting ? "Submitting..." : "Submit Reply"}
                          </ReplyButton>
                        </ReplyActions>
                      </ReplyForm>
                    ) : (
                      <div style={{ marginTop: "12px" }}>
                        <ReplyButton
                          className="secondary"
                          onClick={() => setReplyingTo(q.id)}
                        >
                          Reply to Question
                        </ReplyButton>
                      </div>
                    )}
                  </QuestionCard>
                ))
              ) : (
                <Card style={{ textAlign: "center", padding: "48px 24px" }}>
                  <SectionTitle>No Questions Yet</SectionTitle>
                  <p style={{ color: theme.colors.textMuted }}>
                    No one has asked questions about this task yet.
                  </p>
                </Card>
              )
            ) : current.length > 0 ? (
              current.map((o) => {
                const offerQuestions = offerQuestionsById[o.id] || [];
                const isOfferQuestionsLoading = !!offerQuestionsLoadingById[o.id];
                const isOfferChatExpanded = !!offerChatExpandedById[o.id];
                const offerQuestionText = offerQuestionTextById[o.id] || "";
                const isOfferSubmitting = !!offerQuestionSubmittingById[o.id];
                return (
                <OfferCard key={o.id}>
                  <OfferContent>
                    <OfferHeader>
                      <UserBlock>
                        <AvatarComponent
                          src={o.user.avatar}
                          firstName={o.user.first_name}
                          lastName={o.user.last_name}
                          name={o.user.username}
                          userId={o.user.id}
                          size="40px"
                        />
                        <UserInfo>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              flexWrap: "wrap",
                            }}
                          >
                            <Link to={`/users/${o.user.id}`} style={{ textDecoration: 'none' }}>
                              <UserName style={{ textTransform: "capitalize" }}>
                                {o.user.username ||
                                  `${o.user.first_name} ${o.user.last_name}`}
                              </UserName>
                            </Link>
                            <UserRating
                              userId={o.user.id}
                              averageRating={o.user.average_rating}
                              totalReviews={o.user.total_reviews}
                              style={{ marginLeft: '8px' }}
                            />
                            {o.years_experience && (
                              <ExperienceTag>
                                {o.years_experience} years experience
                              </ExperienceTag>
                            )}
                          </div>
                          <UserMeta>
                            Submitted{" "}
                            {format(parseDate(o.created_at), "MMM d, yyyy")}
                          </UserMeta>
                        </UserInfo>
                      </UserBlock>
                      <StatusBadge status={o.status}>{o.status}</StatusBadge>
                    </OfferHeader>

                    <OfferStatsRow>
                      <StatItem>
                        <StatIcon bg="hsl(145, 63%, 95%)" color="hsl(145, 63%, 42%)">
                          <DollarSign size={16} />
                        </StatIcon>
                        <OfferStatContent>
                          <OfferStatValue>${o.offer_amount}</OfferStatValue>
                          <OfferStatLabel>Proposed Budget</OfferStatLabel>
                        </OfferStatContent>
                      </StatItem>
                      <StatItem>
                        <StatIcon bg="hsl(217, 91%, 95%)" color="hsl(217, 91%, 45%)">
                          <Clock size={16} />
                        </StatIcon>
                        <OfferStatContent>
                          <OfferStatValue>{o.duration || "—"}</OfferStatValue>
                          <OfferStatLabel>Duration</OfferStatLabel>
                        </OfferStatContent>
                      </StatItem>
                      <StatItem>
                        <StatIcon bg="hsl(271, 100%, 95%)" color="hsl(271, 100%, 45%)">
                          <Calendar size={16} />
                        </StatIcon>
                        <OfferStatContent>
                          <OfferStatValue>
                            {o.completion_date
                              ? format(parseDate(o.completion_date), "MMM d")
                              : "—"}
                          </OfferStatValue>
                          <OfferStatLabel>Completion</OfferStatLabel>
                        </OfferStatContent>
                      </StatItem>
                    </OfferStatsRow>

                    <SectionTitle>Proposal</SectionTitle>
                    <SectionContent>
                      <p>{o.proposal}</p>
                    </SectionContent>

                    {o.previous_similar_work && (
                      <>
                        <SectionTitle>Previous Similar Work</SectionTitle>
                        <SectionContent>
                          <p>{o.previous_similar_work}</p>
                        </SectionContent>
                      </>
                    )}

                    {o.portfolio_links && o.portfolio_links.length > 0 && (
                      <PortfolioLinks>
                        <PortfolioLinksTitle>Portfolio Links</PortfolioLinksTitle>
                        <PortfolioLinksList>
                          {o.portfolio_links
                            .filter(link => link && isValidURL(link))
                            .slice(0, 5) // Show more links in manage view
                            .map((link, index) => (
                              <PortfolioLink 
                                key={index}
                                href={link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                              >
                                <ExternalLink />
                                {getDomainName(link)}
                              </PortfolioLink>
                            ))}
                        </PortfolioLinksList>
                      </PortfolioLinks>
                    )}

                    {(offerQuestions.length > 0 || isOfferQuestionsLoading || o.status !== "accepted") && (
                      <OfferChat>
                        <OfferChatHeader>
                          <MessageCircle size={16} />
                          Offer Discussion
                          <OfferChatCount>({offerQuestions.length})</OfferChatCount>
                          <OfferChatToggle
                            onClick={() =>
                              setOfferChatExpandedById((p) => ({
                                ...p,
                                [o.id]: !isOfferChatExpanded,
                              }))
                            }
                          >
                            {offerQuestions.length === 0
                              ? "Start discussion"
                              : isOfferChatExpanded
                              ? "Hide"
                              : "View"}
                          </OfferChatToggle>
                        </OfferChatHeader>
                        {isOfferQuestionsLoading ? (
                          <OfferChatEmpty>Loading messages…</OfferChatEmpty>
                        ) : offerQuestions.length && isOfferChatExpanded ? (
                          <OfferChatMessages>
                            {offerQuestions
                              .flatMap((q) => {
                                const base = [{
                                  id: `q-${q.id}`,
                                  user_id: q.user_id,
                                  user_name: q.user_name,
                                  avatar: q.avatar,
                                  email_verified: q.email_verified,
                                  text: q.question_text || q.text,
                                  created_at: q.created_at,
                                }];
                                const replies = (q.replies || []).map((reply) => ({
                                  id: `r-${reply.id}`,
                                  user_id: reply.user_id,
                                  user_name: reply.user_name,
                                  avatar: reply.avatar,
                                  email_verified: reply.email_verified,
                                  text: reply.reply_text || reply.text,
                                  created_at: reply.created_at,
                                }));
                                return [...base, ...replies];
                              })
                              .sort((a, b) => parseDate(a.created_at) - parseDate(b.created_at))
                              .map((m) => (
                                <OfferChatMessage key={m.id}>
                                  <AvatarComponent
                                    src={m.avatar}
                                    firstName={m.user_name}
                                    userId={m.user_id}
                                    size="28px"
                                  />
                                  <OfferChatBubble>
                                    <OfferChatMeta>
                                      <span>{(m.user_name || "Anonymous User").toLowerCase().replace(/\\b\\w/g, l => l.toUpperCase())}</span>
                                      <span>·</span>
                                      <span>
                                        {formatDistanceToNow(parseDate(m.created_at), {
                                          addSuffix: true,
                                        })}
                                      </span>
                                    </OfferChatMeta>
                                    <OfferChatText>{m.text}</OfferChatText>
                                  </OfferChatBubble>
                                </OfferChatMessage>
                              ))}
                          </OfferChatMessages>
                        ) : null}

                        {isOfferChatExpanded && o.status !== "accepted" ? (
                          <ReplyForm style={{ marginTop: "6px" }}>
                            <ReplyTextarea
                              value={offerQuestionText}
                              onChange={(e) =>
                                setOfferQuestionTextById((p) => ({
                                  ...p,
                                  [o.id]: e.target.value,
                                }))
                              }
                              placeholder="Message about this offer..."
                            />
                            <ReplyActions>
                              <ReplyButton
                                className="primary"
                                onClick={() => handleOfferQuestionSubmit(o.id)}
                                disabled={!offerQuestionText.trim() || isOfferSubmitting}
                              >
                                {isOfferSubmitting ? "Posting..." : "Post Message"}
                              </ReplyButton>
                            </ReplyActions>
                          </ReplyForm>
                        ) : null}
                      </OfferChat>
                    )}

                    {activeTab === "pending" && task.status !== "cancelled" ? (
                      <ActionBar>
                        <QuietBtn onClick={() => handleDecline(o.id)}>
                          Decline
                        </QuietBtn>
                        <PrimaryBtn onClick={() => handleAccept(o.id)}>
                          Accept Offer
                        </PrimaryBtn>
                      </ActionBar>
                    ) : activeTab === "accepted" ? (
                      <ActionBar>
                        {task.status !== "completed" && task.status !== "cancelled" && (
                          <DangerBtn
                            onClick={() => handleCancelCollaboration(o.id)}
                          >
                            Cancel Collaboration
                          </DangerBtn>
                        )}
                        {o.conversation_id && task.status !== "cancelled" ? (
                          <PrimaryBtn
                            onClick={() =>
                              navigate(`/chat/${o.conversation_id}`)
                            }
                          >
                            Go to Project Chat
                          </PrimaryBtn>
                        ) : null}
                      </ActionBar>
                    ) : null}
                  </OfferContent>
                </OfferCard>
              );
              })
            ) : (
              <Card style={{ textAlign: "center", padding: "30px 18px" }}>
                <div
                  style={{
                    fontWeight: 800,
                    marginBottom: 4,
                    color: theme.colors.textHeading,
                  }}
                >
                  No {activeTab} offers
                </div>
                <div style={{ color: theme.colors.textMuted }}>
                  {activeTab === "pending"
                    ? "New offers will appear here when freelancers submit them."
                    : activeTab === "accepted"
                    ? "Accepted offers will appear here."
                    : "Declined offers will appear here."}
                </div>
              </Card>
            )}
          </Container>
        </Page>
      </OuterContainer>

      {/* Reject Reason Modal */}
      {rejectModalOpen && (
        <ModalOverlay onClick={handleRejectCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Decline Offer</ModalTitle>
            <ModalDescription>
              Would you like to provide feedback to help the freelancer improve their future proposals? This is optional but can be valuable for their growth.
            </ModalDescription>
            <ModalTextarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder=""
              maxLength={500}
            />
            <ModalActions>
              <ModalButton className="secondary" onClick={handleRejectCancel}>
                Cancel
              </ModalButton>
              <ModalButton className="primary" onClick={handleRejectSubmit}>
                Decline Offer
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Cancel Task Modal */}
      {cancelModalOpen && (
        <ModalOverlay onClick={handleCancelTaskCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Cancel Task</ModalTitle>
            <ModalDescription>
              Are you sure you want to cancel this task? This action cannot be undone. All pending offers will be automatically declined and any ongoing collaborations will be ended.
            </ModalDescription>
            <ModalActions>
              <ModalButton className="secondary" onClick={handleCancelTaskCancel}>
                Keep Task
              </ModalButton>
              <ModalButton className="primary" onClick={handleCancelTaskConfirm}>
                Cancel Task
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Review Modal */}
      <ReviewModal
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        onSubmit={handleSubmitReview}
        onSkip={() => setReviewModalOpen(false)}
        offers={offers}
        questions={questions}
        taskTitle={task?.title}
        submitting={reviewSubmitting}
        existingReview={existingReview}
      />
    </>
  );
};

export default connect((state) => ({ state }))(ManageTaskPage);
